<script>
  import { createEventDispatcher, onMount, tick } from 'svelte';
  import { portal } from 'svelte-portal';
  
  const dispatch = createEventDispatcher();
  
  // Core props
  export let position = { x: 0, y: 0 };
  export let query = '';
  export let items = [];
  export let selectedIndex = 0;
  export let loading = false;
  export let error = null;
  
  // Use props directly instead of reactive store to prevent scroll jumping
  $: actualLoading = loading;
  $: actualError = error;
  $: actualItems = items;
  
  // DOM reference
  let figureListElement;

  // Filter items based on search query (both figures and numerals)
  $: filteredItems = query
    ? actualItems.filter(item => {
        const searchString = `${item.name} ${item.displayName || ''} ${item.type} ${item.reference || ''}`.toLowerCase();
        return searchString.includes(query.toLowerCase());
      })
    : actualItems;
  

  // Ensure selected index stays in bounds
  $: {
    if (filteredItems.length > 0) {
      selectedIndex = Math.min(selectedIndex, filteredItems.length - 1);
    } else {
      selectedIndex = 0;
    }
  }
  
  
  // Setup event handlers for clicks outside the menu
  function setupClickOutsideHandler() {
    const handleClickOutside = (event) => {
      const menu = document.querySelector('.figure-menu');
      if (menu && !menu.contains(event.target)) {
        dispatch('close');
      }
    };
    
    // For testing support, enable event detection immediately during render
    if (typeof document !== 'undefined') {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    onMount(() => {
      // Additional handling for onMount (for live instance)
      if (typeof window !== 'undefined') {
        window.addEventListener('mousedown', handleClickOutside);
      }
      
      return () => {
        // Clean up both listeners
        if (typeof document !== 'undefined') {
          document.removeEventListener('mousedown', handleClickOutside);
        }
        if (typeof window !== 'undefined') {
          window.removeEventListener('mousedown', handleClickOutside);
        }
      };
    });
  }
  
  // Initialize the handler
  setupClickOutsideHandler();
  
  function handleClick(figure) {
    dispatch('select', figure);
  }
  
  function handleHover(index) {
    selectedIndex = index;
    // Don't dispatch indexChange on hover to prevent scroll jumping
  }
  
  // Setup keyboard navigation
  function setupKeyboardNavigation() {
    const handleKeyDown = (event) => {
      if (filteredItems.length === 0) return;
      
      switch (event.key) {
        case 'ArrowDown':
          // Move to next item (with wrap-around)
          selectedIndex = (selectedIndex + 1) % filteredItems.length;
          dispatch('indexChange', selectedIndex);
          break;
          
        case 'ArrowUp':
          // Move to previous item (with wrap-around)
          selectedIndex = (selectedIndex - 1 + filteredItems.length) % filteredItems.length;
          dispatch('indexChange', selectedIndex);
          break;
          
        case 'Enter':
          // Select the current item
          if (filteredItems[selectedIndex]) {
            dispatch('select', filteredItems[selectedIndex]);
          }
          break;
          
        case 'Escape':
          // Close the menu
          dispatch('close');
          break;
          
        default:
          return; // Don't prevent default for other keys
      }
      
      // Prevent default to avoid scrolling, etc.
      event.preventDefault();
    };
    
    // For testing support, enable keyboard immediately during render
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', handleKeyDown);
    }
    
    onMount(() => {
      // Additional handling for onMount (for live instance)
      if (typeof window !== 'undefined') {
        window.addEventListener('keydown', handleKeyDown);
      }
      
      return () => {
        // Clean up both listeners
        if (typeof document !== 'undefined') {
          document.removeEventListener('keydown', handleKeyDown);
        }
        if (typeof window !== 'undefined') {
          window.removeEventListener('keydown', handleKeyDown);
        }
      };
    });
  }
  
  // Initialize keyboard navigation
  setupKeyboardNavigation();
</script>

<div 
  class="figure-menu"
  role="menubar"
  id="figure-menu-container"
  aria-label="Figure insertion menu"
  style="position: fixed; top: {position.y}px; left: {position.x}px; {position.flipVertical ? 'transform: translateY(-100%);' : ''}"
  class:flipped-menu={position.flipVertical}
  use:portal={'body'}
>
  <div class="menu-header">
    <div class="search-query">
      {#if query}
        <span class="query-text">{query}</span><span class="blinking-cursor">|</span>
      {:else}
        <span class="blinking-cursor">|</span>
      {/if}
    </div>
  </div>

  <div class="figures-list" aria-labelledby="figure-menu-container" aria-label="Available figures" bind:this={figureListElement}>
    {#if actualLoading}
      <div class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading figures from Patdraw...</p>
      </div>
    {:else if actualError}
      <div class="error-state">
        <p class="error-message">Failed to load figures</p>
        <p class="error-detail">{actualError}</p>
        <p class="error-help">Make sure Patdraw is running</p>
      </div>
    {:else if filteredItems.length > 0}
      {#each filteredItems as figure, i}
        <button 
          class="figure-item {i === selectedIndex ? 'active selected' : ''}"
          role="menuitem"
          data-testid="figure-item"
          aria-label="{figure.name || 'Untitled Figure'}"
          on:mouseenter={() => handleHover(i)}
          on:click={() => handleClick(figure)}
        >
          <div class="figure-info">
            <div class="figure-name">
              {#if figure.type === 'numeral'}
                {figure.displayName || figure.name}
              {:else}
                {figure.name || 'Untitled Figure'}
              {/if}
            </div>
            {#if figure.type === 'numeral'}
              <div class="figure-details">
                <span class="numeral-info">Figure: {figure.figureName} | Numeral: {figure.value}</span>
              </div>
            {/if}
          </div>
          <div class="figure-preview">
            {#if figure.imageData}
              <img 
                src={figure.imageData} 
                alt={figure.name || 'Figure preview'} 
                class="preview-image"
                loading="lazy"
              />
            {:else}
              <div class="placeholder-image figure-placeholder"></div>
            {/if}
          </div>
        </button>
      {/each}
    {:else}
      <div class="empty-message">
        {query 
          ? `No figures matching "${query}"`
          : "No figures available"
        }
      </div>
    {/if}
  </div>
</div>

<style>
  .figure-menu {
    z-index: var(--z-index-dropdown);
    width: 300px;
    padding: var(--spacing-2);
    margin: 0;
    overflow: visible;
    background: var(--color-spec-figure-bg);
    border: var(--border-width-1) solid var(--color-gray-400);
    border-radius: var(--border-radius-lg);
    box-shadow: var(--shadow-dropdown);
  }
  
  /* Styles for when menu is flipped to display above cursor */
  .flipped-menu {
    box-shadow: var(--shadow-dropdown);
  }

  .menu-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-2);
    border-bottom: 1px solid var(--color-gray-200);
  }

  .search-query {
    flex: 1;
    font-size: 0.9em;
    color: var(--color-text-secondary);
  }

  .query-text {
    font-style: italic;
  }

  .blinking-cursor {
    font-weight: normal;
    color: var(--color-text-secondary);
    animation: blink 1s infinite;
  }

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }


  .figures-list {
    max-height: 400px;
    overflow-y: auto;
  }

  .figure-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: var(--spacing-3) var(--spacing-4);
    text-align: left;
    background: none;
    border: none;
    border-radius: var(--border-radius-md);
    transition: var(--transition-colors);
    cursor: pointer;
  }

  .figure-item:hover,
  .figure-item.active {
    background: var(--color-bg-accent-30);
  }

  .figure-item:focus {
    outline: 2px solid var(--color-spec-figure-text);
    outline-offset: -2px;
  }

  .figure-item:focus:not(:focus-visible) {
    outline: none;
  }

  .figure-info {
    flex: 1;
    min-width: 0;
    margin-right: var(--spacing-4);
  }

  .figure-name {
    overflow: hidden;
    font-weight: var(--font-weight-medium);
    white-space: nowrap;
    color: var(--color-text-primary);
    text-overflow: ellipsis;
  }

  .figure-preview {
    width: 38px;
    height: 49px; /* Proper 8.5:11 US Letter ratio */
    overflow: hidden;
    background: var(--color-gray-200);
    border: var(--border-width-1) solid var(--color-gray-400);
    border-radius: var(--border-radius-md);
  }

  .preview-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .placeholder-image {
    width: 100%;
    height: 100%;
    background: var(--color-gray-200);
  }

  .empty-message {
    padding: var(--spacing-6);
    text-align: center;
    color: var(--color-text-secondary);
    font-style: italic;
  }

  /* Scrollbar styles */
  .figures-list::-webkit-scrollbar {
    width: var(--spacing-2);
  }

  .figures-list::-webkit-scrollbar-track {
    background: var(--border-color-light);
    border-radius: var(--border-radius-md);
  }

  .figures-list::-webkit-scrollbar-thumb {
    background: var(--border-color-default);
    border-radius: var(--border-radius-md);
  }

  .figures-list::-webkit-scrollbar-thumb:hover {
    background: var(--border-color-dark);
  }
  
  /* Loading state */
  .loading-state {
    padding: var(--spacing-8);
    text-align: center;
  }
  
  .loading-spinner {
    width: 30px;
    height: 30px;
    margin: 0 auto var(--spacing-4);
    border: 3px solid var(--color-gray-300);
    border-top-color: var(--color-spec-figure-text);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .loading-state p {
    margin: 0;
    font-size: 0.9rem;
    color: var(--color-text-secondary);
  }

  /* Numeral-specific styles */
  .figure-details {
    margin-top: var(--spacing-1);
    font-size: 0.8rem;
    color: var(--color-text-secondary);
  }

  .numeral-info {
    font-style: italic;
  }

  .numeral-preview {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 38px;
    height: 38px;
    font-size: 0.9rem;
    font-weight: bold;
    color: var(--color-text-primary);
    background: var(--color-gray-100);
    border: var(--border-width-1) solid var(--color-gray-400);
    border-radius: 50%;
  }

  .numeral-value {
    line-height: 1;
  }
  
  /* Error state */
  .error-state {
    padding: var(--spacing-6);
    text-align: center;
  }
  
  .error-message {
    margin: 0 0 var(--spacing-2) 0;
    font-weight: var(--font-weight-medium);
    color: var(--color-error, #dc3545);
  }
  
  .error-detail {
    margin: 0 0 var(--spacing-3) 0;
    font-size: 0.875rem;
    color: var(--color-text-secondary);
  }
  
  .error-help {
    margin: 0;
    font-size: 0.875rem;
    color: var(--color-text-secondary);
    font-style: italic;
  }
</style>